import type { SlotDefinition, WorkspaceMask } from '../../lib/wstypes'

/**
 * Confirm what the homeowner selected (07 §2 "Labeling").
 *
 * A small vision model proposes a slot from the cut-out; this is where a person confirms it.
 * The distinction matters more than it looks: the slot decides the render path, the labour
 * rate and which products the rail may offer, so a wrong guess silently applied is a bathroom
 * fan priced as a vanity.
 *
 * The proposal leads, the alternatives follow, and "something else" opens the full list —
 * three taps at worst, one in the normal case.
 */

interface Props {
  mask: WorkspaceMask
  slots: SlotDefinition[]
  category: string
  coverage: Record<string, number>
  onConfirm: (slot: string) => void
  onDelete: () => void
}

export function SlotChips({ mask, slots, category, coverage, onConfirm, onDelete }: Props) {
  const byId = new Map(slots.map((slot) => [slot.id, slot]))
  const proposed = mask.slot ? byId.get(mask.slot) : undefined

  // Slots that belong in this room and have something behind them. A chip for a slot with no
  // approved products is a chip that leads to an empty rail.
  const candidates = slots
    .filter((slot) => slot.room === 'any' || slot.room === category)
    .filter((slot) => slot.id !== mask.slot)
    .sort((a, b) => (coverage[b.id] ?? 0) - (coverage[a.id] ?? 0))
    .slice(0, 6)

  return (
    <div className="rounded-card border border-line bg-white p-4">
      <p className="m-0 text-sm text-body">
        {proposed ? (
          <>
            Looks like a <strong className="text-ink">{proposed.name.toLowerCase()}</strong>. Is
            that right?
          </>
        ) : (
          <>What did you select?</>
        )}
      </p>

      <div className="mt-3 flex flex-wrap gap-2">
        {proposed && (
          <button
            type="button"
            onClick={() => onConfirm(proposed.id)}
            className="min-h-11 rounded-pill bg-blue px-4 text-sm font-semibold text-white shadow-blue"
          >
            {proposed.name} ✓
          </button>
        )}
        {candidates.map((slot) => (
          <button
            key={slot.id}
            type="button"
            onClick={() => onConfirm(slot.id)}
            className="min-h-11 rounded-pill border border-line bg-white px-4 text-sm font-semibold text-ink hover:border-blue"
          >
            {slot.name}
            {coverage[slot.id] ? (
              <span className="ml-1.5 font-mono text-xs text-muted">{coverage[slot.id]}</span>
            ) : null}
          </button>
        ))}
        <button
          type="button"
          onClick={onDelete}
          className="ml-auto min-h-11 rounded-pill border border-line bg-white px-4 text-sm font-semibold text-blue-press"
        >
          Remove selection
        </button>
      </div>
    </div>
  )
}
